import React from 'react';
import { ImageMedia } from './types';
import { CanvasRendererForImage } from './CanvasRendererForImage';
import { MediaRole, normalizeMediaRole } from '../MediaRole';
import { EffectType } from '../effects';
import { CropBox } from '../CropBox';
import { ObjectFit } from '../types/style';
import {
    SerializableState,
    ImageMediaState,
    Serializable
} from '../types/serializable';
import { validateMediaState } from '../schemas/timeline-schema';
import { deserializeEffects, serializeEffects } from '../effects/serialization';

export class ImageMediaImpl implements ImageMedia, Serializable {
    id: string;
    type: 'image';
    role: MediaRole;
    src: string;
    effects: EffectType[];
    crop?: CropBox;
    duration?: number;

    constructor(
        id: string,
        role: MediaRole,
        src: string,
        effects: EffectType[] = [],
        crop?: CropBox,
        duration?: number
    ) {
        this.id = id;
        this.type = 'image';
        this.role = role;
        this.src = src;
        this.effects = effects;
        this.crop = crop;
        this.duration = duration;
    }

    /**
     * Export ImageMedia to serializable state
     */
    exportState(): ImageMediaState {
        const state: ImageMediaState = {
            type: 'ImageMedia',
            version: '1.0.0',
            id: this.id,
            src: this.src
        };

        // Add optional properties
        if (this.role !== undefined) state.role = this.role;
        if (this.duration !== undefined) state.duration = this.duration;
        if (this.effects !== undefined && this.effects.length > 0) {
            state.effects = serializeEffects(this.effects);
        }
        if (this.crop !== undefined) state.crop = this.crop;

        return state;
    }

    /**
     * Import ImageMedia from serialized state
     */
    static importState(state: SerializableState): ImageMediaImpl {
        const validatedState = validateMediaState(state);
        if (validatedState.type !== 'ImageMedia') {
            throw new Error(`Expected ImageMedia state, received ${validatedState.type}`);
        }

        return new ImageMediaImpl(
            validatedState.id,
            normalizeMediaRole(validatedState.role),
            validatedState.src,
            deserializeEffects(validatedState.effects),
            validatedState.crop,
            validatedState.duration
        );
    }

    render(fps: number, blockId: string, duration: number, objectFit?: ObjectFit) {
        return (
            <CanvasRendererForImage
                src={this.src}
                effects={this.effects}
                style={{ objectFit: objectFit ?? 'cover' }}
            />
        );
    }
}
